import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UserFile } from 'src/app/files/model/user-file';
import { UserFileStatus } from 'src/app/files/model/user-file-status';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { FileType } from 'src/app/shared/model/file-type';
import { toVisibleDateTimeFormat } from 'src/app/shared/util/date-util';
import { AlcoholTestRequestDeliveredResponse } from 'src/app/vehicles/model/alcohol-test/alcohol-test-request-delivered-response';
import { AlcoholLockReport } from 'src/app/vehicles/model/live-data-report/alcohol-lock-report';
import { LiveDataReport } from 'src/app/vehicles/model/live-data-report/live-data-report';
import { PanicButtonReport } from 'src/app/vehicles/model/live-data-report/panic-button-report';
import { VehicleOffCourseReport } from 'src/app/vehicles/model/live-data-report/vehicle-off-course-report';
import { SnapshtFileCreatedDto } from 'src/app/vehicles/service/snapshot-notifier-service';
import { Notification, NotificationType } from '../model/notification';
import { LangService } from './lang.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private translate: TranslateService, private langService: LangService, private settings: UserSettingsService) { }

  getNotificationText(notification: Notification): Observable<string | null> {
    switch (notification.type) {
      case NotificationType.FILE:
        return this.getUserFileText(notification.data);
      case NotificationType.ALCOHOL_LOCK_NEGATIVE_RESULT:
        return this.getAlcoholLockNegativeResultText(notification.data);
      case NotificationType.PANIC_BUTTON:
        return this.getPanicButtonText(notification.data);
      case NotificationType.VEHICLE_OFF_COURSE:
        return this.getVehicleOffCourseText(notification.data);
      case NotificationType.SNAPSHOT_FILE:
        return this.getSnapshotFileText(notification.data).pipe(tap(data => console.log(data)));
      case NotificationType.ALCOHOL_TEST_REQUEST_DELIVERED:
        return this.getAlcoholTestResponse(notification.data).pipe(tap(data => console.log(data)));
      default:
        return of(null);
    }
  }

  getUserFileTitle(userFile: UserFile): Observable<string | null> {
    const isChart = userFile.fileType == FileType.CHART;
    switch (userFile.status) {
      case UserFileStatus.DONE:
        return this.langService.translateNested('reportCreated', { fileOrChart: isChart ? 'chart' : 'file' });
      case UserFileStatus.FAILED:
        return this.langService.translateNestedSingle('reportCreationError', 'reportOrChart2', isChart ? 'chart' : 'report');
    }
    return of(null);
  }

  getUserFileText(userFile: UserFile): Observable<string | null> {
    const reportType = userFile.sourceTable;
    const isChart = userFile.fileType == FileType.CHART;

    switch (userFile.status) {
      case UserFileStatus.DONE:
        return this.langService.translateNested('reportCreatedSuccessfully',
          { fileOrChart: isChart ? 'chart' : 'file', fileName: userFile.fileName, filenameExtension: isChart ? '' : '.' + userFile.fileType });
      case UserFileStatus.FAILED:
        return this.langService.translateNestedSingle('reportCreationErrorDetails', 'reportType', reportType);
    }
    return of(null);
  }

  getAlcoholLockNegativeResultText(report: LiveDataReport<AlcoholLockReport>): Observable<string> {
    return this.translate.get('alcoholTestFailedWarning', { sideNumber: report.sideNumber });
  }

  getPanicButtonText(report: LiveDataReport<PanicButtonReport>): Observable<string> {
    const reportData: PanicButtonReport = report.reportData;
    if (this.settings.isPanicButtonCameraPreviewEnabled() && reportData.previewUrl)
      return this.translate.get('panicButtonAlertText', { sideNumber: report.sideNumber });
    else
      return this.translate.get('panicButtonUsedInVehicle', { sideNumber: report.sideNumber });
  }

  getVehicleOffCourseText(report: LiveDataReport<VehicleOffCourseReport>): Observable<string> {
    return this.translate.get('off_course_warn', { sideNumber: report.sideNumber });
  }

  getSnapshotFileText(dto: SnapshtFileCreatedDto): Observable<string> {
    return this.translate.get('snapshotFileReceived', { vehicle: dto?.sideNumber ?? 'error' });
  }

  getAlcoholTestResponse(response: AlcoholTestRequestDeliveredResponse): Observable<string> {
    return this.translate.get(
      'alcoholTestResponseReceived',
      { sideNumber: response.sideNumber, receiveTime: toVisibleDateTimeFormat(moment(response.receiveTime)) }
    );
  }

}
